import axios, { AxiosResponse } from 'axios'
import type {
  SearchParams,
  SearchResponse,
  SavedAd,
  CompleteSearch,
  CompleteSearchListItem,
  CompleteSearchWithPagination,
  TrackedPage,
  AIsuggestion,
  SearchHistoryResponse,
  SearchHistoryStats,
  SearchStats,
  ApiResponse,
  AuthRequest,
  RegisterRequest,
  AuthResponse,
  User,
  TrackedAdvertiser,
  TrackedAdvertiserResponse,
  TrackedAd,
  TrackedAdvertiserStats
} from '../types/shared'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 900000, // búsquedas largas (hasta 15 min)
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    // El token se pone en el arranque, dentro de un efecto. SearchProvider es hijo y su
    // efecto corre antes: la primera llamada salía sin Authorization, recibía 401 y la
    // búsqueda se quedaba con las stats del anunciante apagadas toda la sesión.
    if (!config.headers.Authorization && typeof localStorage !== 'undefined') {
      const token =
        localStorage.getItem('ads_finder_impersonation_token') ||
        localStorage.getItem('ads_finder_token')
      if (token) config.headers.Authorization = `Bearer ${token}`
    }
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`)
    return config
  },
  (error) => {
    console.error('❌ Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for logging and error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`)
    return response
  },
  (error) => {
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN'
    const url = error.config?.url || 'unknown'
    const status = error.response?.status || 'Network Error'
    
    console.error(`❌ Response Error: ${method} ${url} - ${status}`)
    
    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      console.warn('🔐 Unauthorized request - token may be invalid')
    }
    
    // Handle network errors (server restart, etc.)
    if (!error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED')) {
      console.warn('🔄 Network error detected - server may be restarting')
    }
    
    console.error('❌ Response Error:', error.response?.data || error.message)
    return Promise.reject(error)
  }
)

// === SEARCH API ===
export const searchApi = {
  // Main search endpoint
  search: async (params: SearchParams): Promise<SearchResponse> => {
    const response = await api.post('/search', params)
    return response.data
  },

  // Fetch multiple pages
  fetchMultiplePages: async (initialUrl: string, maxPages: number = 5): Promise<SearchResponse> => {
    const response = await api.get('/search/multiple-pages', {
      params: { initialUrl, maxPages }
    })
    return response.data
  },
}

// === SAVED ADS API ===
export const savedAdsApi = {
  // Get saved ads with filters
  getSavedAds: async (params?: {
    folderId?: string | null
    sortBy?: string
    limit?: number
  }) => {
    const response = await api.get('/saved-ads', { params })
    return response.data
  },

  // Save a single ad
  saveAd: async (data: {
    adData: any
    tags?: string[]
    notes?: string
    collection?: string
    folderId?: string
  }): Promise<SavedAd> => {
    const response = await api.post('/saved-ads', data)
    return response.data
  },

  // Update saved ad
  updateSavedAd: async (id: string, data: {
    tags?: string[]
    notes?: string
    collection?: string
    folderId?: string
    isFavorite?: boolean
  }): Promise<ApiResponse> => {
    const response = await api.put(`/saved-ads/${id}`, data)
    return response.data
  },

  // Move saved ad to another folder
  moveSavedAd: async (id: string, data: {
    folderId?: string
    collection?: string
  }): Promise<ApiResponse> => {
    const response = await api.put(`/saved-ads/${id}/move`, data)
    return response.data
  },

  // Get available collections
  getCollections: async () => {
    const response = await api.get('/saved-ads/collections')
    return response.data
  },

  // Get available tags
  getTags: async () => {
    const response = await api.get('/saved-ads/tags')
    return response.data
  },

  // Bulk save ads
  bulkSaveAds: async (data: {
    ads: any[]
    defaultTags?: string[]
    defaultCollection?: string
    defaultFolderId?: string
    defaultNotes?: string
  }) => {
    const response = await api.post('/saved-ads/bulk', data)
    return response.data
  },

  // Delete saved ad
  deleteSavedAd: async (id: string): Promise<ApiResponse> => {
    const response = await api.delete(`/saved-ads/${id}`)
    return response.data
  },
}

// === COMPLETE SEARCHES API ===
export const completeSearchesApi = {
  // Get all complete searches
  getCompleteSearches: async (params?: {
    sortBy?: string
    limit?: number
  }): Promise<{
    searches: CompleteSearchListItem[]
    stats: any
  }> => {
    const response = await api.get('/complete-searches', { params })
    return response.data
  },

  // Get specific complete search with pagination
  getCompleteSearch: async (
    id: string,
    params?: { page?: number; limit?: number }
  ): Promise<CompleteSearchWithPagination> => {
    const response = await api.get(`/complete-searches/${id}`, { params })
    return response.data
  },

  // Save complete search
  saveCompleteSearch: async (data: {
    searchName: string
    searchParams: any
    results: any[]
    source: string
    metadata?: any
  }): Promise<CompleteSearch> => {
    const response = await api.post('/complete-searches', data)
    return response.data
  },

  // Delete complete search
  deleteCompleteSearch: async (id: string): Promise<ApiResponse> => {
    const response = await api.delete(`/complete-searches/${id}`)
    return response.data
  },

  // Search in saved searches
  searchCompleteSearches: async (params: {
    q?: string
    source?: string
    country?: string
    minResults?: number
  }) => {
    const response = await api.get('/complete-searches/search', { params })
    return response.data
  },

  // Get detailed statistics
  getStats: async (): Promise<SearchStats> => {
    const response = await api.get('/complete-searches/stats')
    return response.data
  },
}

// === FOLDERS API ===
export const foldersApi = {
  // Get all folders
  getFolders: async () => {
    const response = await api.get('/folders')
    return response.data
  },

  // Get folders as tree
  getFoldersTree: async () => {
    const response = await api.get('/folders/tree')
    return response.data
  },

  // Get folder limits
  getFolderLimits: async () => {
    const response = await api.get('/folders/limits')
    return response.data
  },

  // Create folder
  createFolder: async (data: {
    name: string
    parentId?: string | null
    color?: string
    icon?: string
  }) => {
    const response = await api.post('/folders', data)
    return response.data
  },

  // Update folder
  updateFolder: async (id: string, data: {
    name?: string
    color?: string
    icon?: string
  }) => {
    const response = await api.put(`/folders/${id}`, data)
    return response.data
  },

  // Move folder
  moveFolder: async (id: string, data: {
    newParentId: string | null
  }) => {
    const response = await api.post(`/folders/${id}/move`, data)
    return response.data
  },

  // Delete folder
  deleteFolder: async (id: string, moveAdsToFolderId?: string) => {
    const params = moveAdsToFolderId ? { moveAdsToFolderId } : {}
    const response = await api.delete(`/folders/${id}`, { params })
    return response.data
  },

  // Refresh folder counts
  refreshFolderCounts: async () => {
    const response = await api.post('/folders/refresh-counts')
    return response.data
  }
}

// === TRACKED PAGES API ===
export const trackedPagesApi = {
  // Get all tracked pages
  getTrackedPages: async (): Promise<TrackedPage[]> => {
    const response = await api.get('/pages')
    return response.data
  },

  // Add tracked page
  addTrackedPage: async (pageIdentifier: string): Promise<TrackedPage> => {
    const response = await api.post('/pages', { pageIdentifier })
    return response.data
  },

  // Remove tracked page
  removeTrackedPage: async (id: string): Promise<ApiResponse> => {
    const response = await api.delete(`/pages/${id}`)
    return response.data
  },
}

// === AI SUGGESTIONS API ===
export const suggestionsApi = {
  // Generate keyword suggestions
  generateSuggestions: async (idea: string): Promise<AIsuggestion> => {
    const response = await api.post('/suggestions', { idea })
    return response.data
  },

  // Check AI service health
  checkHealth: async () => {
    const response = await api.get('/suggestions/health')
    return response.data
  },
}

// === AUTHENTICATION API ===
export const authApi = {
  // Register new user
  register: async (userData: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', userData)
    return response.data
  },

  // Login user
  login: async (credentials: AuthRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials)
    return response.data
  },

  // Get current user
  me: async (): Promise<{ success: boolean; user: User }> => {
    const response = await api.get('/auth/me')
    return response.data
  },

  // Verify token
  verifyToken: async (token: string): Promise<{ success: boolean; user: User }> => {
    const response = await api.post('/auth/verify', { token })
    return response.data
  },

  // Logout
  logout: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/auth/logout')
    return response.data
  }
}

// === UTILITY FUNCTIONS ===
export const apiUtils = {
  // Health check
  healthCheck: async () => {
    const response = await api.get('/health')
    return response.data
  },

  // Get proxy image
  getProxyImageUrl: (imageUrl: string): string => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    return `${baseUrl}/proxy-image?url=${encodeURIComponent(imageUrl)}`
  },


  // Set authorization token
  setAuthToken: (token: string | null) => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete api.defaults.headers.common['Authorization']
    }
  },

  // Generic error handler
  handleApiError: (error: any): string => {
    // First try to get the error from response data
    if (error.response?.data?.error) {
      return error.response.data.error
    }
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    // Fallback to the axios error message
    if (error.message) {
      return error.message
    }
    return 'An unexpected error occurred'
  }
}

// === SCRAPER API ===
export const scraperApi = {
  // Scrape all ads from a specific advertiser
  scrapeAdvertiser: async (params: {
    advertiserName: string
    maxAds?: number
    country?: string
    useStealth?: boolean
  }): Promise<SearchResponse> => {
    const response = await api.post('/ads/scrape-advertiser', params)
    return response.data
  },

  /** Misma flag que ENABLE_ADVERTISER_STATS en el backend */
  getAdvertiserStatsStatus: async (): Promise<{ enabled: boolean }> => {
    const response = await api.get('/ads/advertiser-stats-status')
    return response.data
  },

  // Get total active ads count for a page by pageId
  getAdvertiserStats: async (params: {
    pageId: string
    country?: string
  }): Promise<{
    success: boolean
    pageId: string
    advertiserName?: string
    totalActiveAds?: number
    advertiserStatsUnavailable?: boolean
    lastUpdated?: string
    executionTime: number
    message: string
    debug?: any
  }> => {
    const response = await api.post('/ads/advertiser-stats', params)
    return response.data
  },

  // Cancel pending jobs for current user
  cancelPendingJobs: async (): Promise<{
    success: boolean
    message: string
    cancelledCount: number
    remainingJobs: number
    userId: string
  }> => {
    const response = await api.post('/ads/cancel-pending', {}, {
      timeout: 500 // 500ms timeout - very fast
    })
    return response.data
  },

}

// === SEARCH HISTORY API ===
export const searchHistoryApi = {
  // Get user's search history
  getHistory: async (params?: {
    page?: number
    limit?: number
    search?: string
    country?: string
    dateFrom?: string
    dateTo?: string
  }): Promise<any> => {
    const response = await api.get('/search-history', { 
      params,
      timeout: 15000 // 15 second timeout
    })
    return response.data
  },

  // Get search statistics
  getStats: async (period?: string): Promise<any> => {
    const response = await api.get('/search-history/stats', { 
      params: period ? { period } : {},
      timeout: 20000 // 20 second timeout
    })
    return response.data
  },

  // Note: Delete functions removed for audit and limit control purposes
}

// ===== TRACKED ADVERTISERS API =====
export const trackedAdvertisersApi = {
  // Get tracked advertisers with pagination
  getTrackedAdvertisers: async (page = 1, limit = 20, active = 'true'): Promise<TrackedAdvertiserResponse> => {
    const response = await api.get('/tracked-advertisers', {
      params: { page, limit, active }
    })
    return response.data
  },

  // Get tracking statistics
  getStats: async (): Promise<{ success: boolean; data: TrackedAdvertiserStats }> => {
    const response = await api.get('/tracked-advertisers/stats')
    return response.data
  },

  // Add advertiser to tracking
  addAdvertiser: async (advertiserData: {
    pageId: string;
    pageName: string;
    pageProfileUri?: string;
    pageProfilePictureUrl?: string;
    pageLikeCount?: number;
    pageCategories?: string[];
    pageVerification?: boolean;
    productType: 'physical' | 'digital';
    notes?: string;
    initialActiveAdsCount?: number;
  }): Promise<{ success: boolean; data: TrackedAdvertiser; message: string }> => {
    const response = await api.post('/tracked-advertisers', advertiserData)
    return response.data
  },

  // Update tracked advertiser
  updateAdvertiser: async (id: string, updateData: Partial<TrackedAdvertiser>): Promise<{ success: boolean; data: TrackedAdvertiser; message: string }> => {
    const response = await api.put(`/tracked-advertisers/${id}`, updateData)
    return response.data
  },

  // Delete tracking
  deleteTracking: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/tracked-advertisers/${id}`)
    return response.data
  },

  // Get all ads for an advertiser
  getAds: async (id: string, active?: string, page = 1, limit = 50): Promise<{
    success: boolean;
    data: TrackedAd[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await api.get(`/tracked-advertisers/${id}/ads`, {
      params: { active, page, limit }
    })
    return response.data
  },

  // Get ad changes for today
  getAdChanges: async (id: string): Promise<{
    success: boolean;
    data: {
      newAds: TrackedAd[];
      deactivatedAds: TrackedAd[];
      reactivatedAds: TrackedAd[];
      stats: {
        newCount: number;
        deactivatedCount: number;
        reactivatedCount: number;
      };
    };
  }> => {
    const response = await api.get(`/tracked-advertisers/${id}/ads/changes`)
    return response.data
  },

  // Get specific ad details
  getAdDetails: async (id: string, adId: string): Promise<{
    success: boolean;
    data: TrackedAd;
  }> => {
    const response = await api.get(`/tracked-advertisers/${id}/ads/${adId}`)
    return response.data
  },

  // Get duplicates
  getDuplicates: async (id: string): Promise<{
    success: boolean;
    data: Array<{
      _id: string;
      ads: TrackedAd[];
      count: number;
    }>;
    totalGroups: number;
    totalDuplicates: number;
  }> => {
    const response = await api.get(`/tracked-advertisers/${id}/duplicates`)
    return response.data
  },

}

export default api

// === CAPTURAS AUTOMÁTICAS (anuncios en fuego) ===
export const capturedAdsApi = {
  list: async (params?: {
    status?: string
    trigger?: string
    product?: string
    /** Texto libre: anunciante, término de origen, oferta o copy del anuncio. */
    q?: string
    /** Tipo de producto concreto: ebook, curso, plantilla… */
    kind?: string
    /** Copias mínimas del creativo. */
    minCopies?: number
    /** Anuncios activos mínimos del ANUNCIANTE, que es otra cosa que las copias. */
    minAdvertiserAds?: number
    /** Días mínimos corriendo. */
    minDays?: number
    /** Solo lo capturado en las últimas N horas. */
    desdeHoras?: number
    grouped?: boolean
    sortBy?: string
    minVelocity?: number
    /** 'only' trae SOLO los descartados; true, unos y otros. */
    includeDismissed?: boolean | 'only'
    /** Entre los descartados, de quién fue la mano. */
    dismissedBy?: 'usuario' | 'clasificador'
    limit?: number
    page?: number
  }) => {
    const response = await api.get('/captured-ads', { params })
    return response.data
  },

  /** El resumen acepta los MISMOS filtros que el listado, para que ambos hablen de lo mismo. */
  stats: async (params?: {
    status?: string
    trigger?: string
    product?: string
    q?: string
    kind?: string
    minCopies?: number
    minAdvertiserAds?: number
    minDays?: number
    /** Solo lo capturado en las últimas N horas. */
    desdeHoras?: number
    minVelocity?: number
    includeDismissed?: boolean | 'only'
    dismissedBy?: 'usuario' | 'clasificador'
  }) => {
    const response = await api.get('/captured-ads/stats', { params })
    return response.data
  },

  detail: async (id: string) => {
    const response = await api.get(`/captured-ads/${id}`)
    return response.data
  },

  /** Echa al anunciante entero: todas sus capturas y el minero deja de seguirlo. */
  dismissAdvertiser: async (pageId: string) => {
    const response = await api.delete(`/captured-ads/advertiser/${pageId}`)
    return response.data
  },

  getSettings: async () => {
    const response = await api.get('/captured-ads/settings')
    return response.data
  },

  updateSettings: async (settings: Record<string, number | boolean>) => {
    const response = await api.put('/captured-ads/settings', settings)
    return response.data
  },

  promote: async (id: string, data?: { folderId?: string; tags?: string[]; notes?: string }) => {
    const response = await api.post(`/captured-ads/${id}/promote`, data || {})
    return response.data
  },

  dismiss: async (id: string) => {
    const response = await api.delete(`/captured-ads/${id}`)
    return response.data
  },

  /** Deshace un descarte: la captura vuelve a la rejilla y su semilla recupera el punto. */
  restore: async (id: string) => {
    const response = await api.post(`/captured-ads/${id}/restore`)
    return response.data
  }
}

// === MINERO AUTÓNOMO ===
export const minerApi = {
  status: async () => (await api.get('/miner/status')).data,
  start: async () => (await api.post('/miner/start')).data,
  stop: async () => (await api.post('/miner/stop')).data,
  sweep: async () => (await api.post('/miner/sweep')).data,

  getSettings: async () => (await api.get('/miner/settings')).data,
  updateSettings: async (settings: Record<string, unknown>) =>
    (await api.put('/miner/settings', settings)).data,

  seeds: async (state?: string) => (await api.get('/miner/seeds', { params: { state } })).data,
  addSeeds: async (data: { terms: string[]; countries?: string[]; sortBy?: string }) =>
    (await api.post('/miner/seeds', data)).data,
  setSeedState: async (id: string, state: 'active' | 'paused') =>
    (await api.patch(`/miner/seeds/${id}`, { state })).data,
  deleteSeed: async (id: string) => (await api.delete(`/miner/seeds/${id}`)).data,

  runs: async (limit = 20) => (await api.get('/miner/runs', { params: { limit } })).data
}
